import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { formatDateTimeValue, formatDateValue } from '@/lib/dates';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const courseStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export const programStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

export const publishStatusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

export const liveSessionStatusOptions = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'live', label: 'Live' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const gradeStatusOptions = [
  { value: 'Completed', label: 'Completed' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Withdrawn', label: 'Withdrawn' },
];

export const LETTER_GRADES = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'F', 'P', 'W', 'CIP', 'R'];

export const GRADE_COLORS = {
  'A+': 'bg-green-100 text-green-800',
  A: 'bg-green-100 text-green-800',
  'A-': 'bg-green-100 text-green-700',
  'B+': 'bg-blue-100 text-blue-800',
  B: 'bg-blue-100 text-blue-800',
  'B-': 'bg-blue-100 text-blue-700',
  'C+': 'bg-yellow-100 text-yellow-800',
  C: 'bg-yellow-100 text-yellow-800',
  'C-': 'bg-orange-100 text-orange-700',
  'D+': 'bg-orange-100 text-orange-800',
  D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-700',
  P: 'bg-green-50 text-green-600',
  W: 'bg-gray-100 text-gray-600',
  CIP: 'bg-purple-100 text-purple-600',
  R: 'bg-gray-100 text-gray-500',
};

export const GPA_MAP = {
  'A+': 4.33,
  A: 4.0,
  'A-': 3.67,
  'B+': 3.33,
  B: 3.0,
  'B-': 2.67,
  'C+': 2.33,
  C: 2.0,
  'C-': 1.67,
  'D+': 1.33,
  D: 1.0,
  F: 0,
};

export function labelize(value) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Not set';
}

export function formatDate(value) {
  return formatDateValue(value, {
    fallback: '-',
    locale: 'en-CA',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(value) {
  return formatDateTimeValue(value, {
    fallback: '-',
    locale: 'en-CA',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function statusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'published' || normalized === 'graded' || normalized === 'live' || normalized === 'completed') {
    return 'border-0 bg-green-100 text-green-700 hover:bg-green-100';
  }
  if (normalized === 'submitted' || normalized === 'draft' || normalized === 'upcoming' || normalized === 'in progress') {
    return 'border-0 bg-yellow-100 text-yellow-700 hover:bg-yellow-100';
  }
  if (normalized === 'cancelled' || normalized === 'archived' || normalized === 'withdrawn') {
    return 'border-0 bg-gray-100 text-gray-600 hover:bg-gray-100';
  }
  return 'border-0 bg-[#062B1A]/5 text-[#062B1A]/70 hover:bg-[#062B1A]/5';
}

export function StatusBadge({ value, className = '' }) {
  return (
    <Badge className={`text-[10px] ${statusBadgeClass(value)} ${className}`}>
      {labelize(value)}
    </Badge>
  );
}

export function InlineSelect({ value, options, disabled, onChange, className = 'w-[150px]' }) {
  return (
    <Select value={value || options[0]?.value} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger
        className={`h-9 border-[#062B1A]/15 ${className}`}
        onClick={(event) => event.stopPropagation()}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ConfirmDeleteDialog({
  open,
  title,
  description,
  count,
  deleting,
  onOpenChange,
  onConfirm,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleting}
            onClick={onConfirm}
            className="bg-red-700 text-white hover:bg-red-800"
          >
            {deleting ? 'Deleting...' : `Delete ${count}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function courseLabel(course) {
  if (!course) return 'Unknown course';
  return `${course.course_code ? `${course.course_code} - ` : ''}${course.title || 'Untitled course'}`;
}
